import { useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { KeyRound, Lock, LogOut, MoreHorizontal, Pencil, Plus, Power, ShieldCheck, ShieldOff, UserPlus } from 'lucide-react'
import { ROLES, ROLE_LABELS } from '@tumin/domain'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { PasswordConfirmDialog } from '@/components/password-confirm-dialog'
import { DataTable, type Column } from '@/components/data-table'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { NativeSelect } from '@/components/ui/native-select'
import type { Branch } from '@/features/admin/branches/branch-dialog'
import { EmployeeDialog } from '@/features/admin/users/employee-dialog'
import { ResetPasswordDialog } from '@/features/admin/users/reset-password-dialog'
import type { Employee } from '@/features/admin/users/types'
import { twoFactorApi } from '@/features/security/api'
import { api } from '@/lib/api'
import { useCurrentEmployee } from '@/lib/auth'
import { formatRelative, EMPTY_VALUE } from '@/lib/dates'
import { notifySuccess } from '@/lib/notify'
import { queryKeys } from '@/lib/query-keys'
import { requirePermission } from '@/lib/route-guards'
import { useShortcuts, type ShortcutDef } from '@/lib/shortcuts'
import { useServerTable } from '@/lib/use-server-table'

export const Route = createFileRoute('/_app/admin/users')({
  beforeLoad: requirePermission('employees:manage'),
  component: UsersPage,
})

/** Valor del filtro que significa "sin filtrar". */
const ANY = ''

function UsersPage() {
  const queryClient = useQueryClient()
  const me = useCurrentEmployee()
  const [role, setRole] = useState(ANY)
  const [active, setActive] = useState(ANY)
  const [dialog, setDialog] = useState<{ open: boolean; employee: Employee | null }>({ open: false, employee: null })
  const [resetting, setResetting] = useState<Employee | null>(null)
  const [toggling, setToggling] = useState<Employee | null>(null)
  const [resettingTwoFactor, setResettingTwoFactor] = useState<Employee | null>(null)
  const [revokingSessions, setRevokingSessions] = useState<Employee | null>(null)

  const table = useServerTable<Employee>({
    queryKey: queryKeys.employees.all,
    path: '/employees',
    initialSort: { columnId: 'fullName', dir: 'asc' },
    params: { role: role || undefined, active: active || undefined },
  })
  const { data: branches = [] } = useQuery({
    queryKey: queryKeys.branches.all,
    queryFn: ({ signal }) => api.get<Branch[]>('/branches', undefined, signal),
  })
  const branchName = useMemo(() => new Map(branches.map((b) => [b.id, b.name])), [branches])

  const toggleActive = useMutation({
    mutationFn: (employee: Employee) => api.patch(`/employees/${employee.id}`, { isActive: !employee.isActive }),
    meta: { silentError: true },
    onSuccess: (_data, employee) => {
      notifySuccess(employee.isActive ? 'Usuario desactivado' : 'Usuario activado')
      void queryClient.invalidateQueries({ queryKey: queryKeys.employees.all })
    },
  })

  const openNew = () => setDialog({ open: true, employee: null })
  const shortcuts = useMemo<ShortcutDef[]>(() => [{ id: 'user-new', group: 'Acciones', keys: ['N'], sequence: ['n'], label: 'Nuevo usuario', run: openNew }], [])
  useShortcuts(shortcuts)

  const actions = (employee: Employee) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" aria-label={`Acciones para ${employee.fullName}`} onClick={(e) => e.stopPropagation()}>
          <MoreHorizontal />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuItem onClick={() => setDialog({ open: true, employee })}>
          <Pencil /> Editar
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => setResetting(employee)}>
          <KeyRound /> Restablecer contraseña
        </DropdownMenuItem>
        {employee.id !== me?.id && employee.twoFactorEnabled && (
          <DropdownMenuItem onClick={() => setResettingTwoFactor(employee)}>
            <ShieldOff /> Restablecer verificación en dos pasos
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => setRevokingSessions(employee)}>
          <LogOut /> Cerrar todas sus sesiones
        </DropdownMenuItem>
        {employee.id !== me?.id && (
          <DropdownMenuItem onClick={() => setToggling(employee)} className={employee.isActive ? 'text-destructive focus:text-destructive' : undefined}>
            <Power /> {employee.isActive ? 'Desactivar' : 'Activar'}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  const locked = (employee: Employee) => employee.lockedUntil !== null && new Date(employee.lockedUntil) > new Date()

  const columns: Column<Employee>[] = [
    {
      id: 'fullName',
      header: 'Nombre',
      sortable: true,
      cell: (e) => (
        <div className="min-w-0">
          <p className="flex items-center gap-1.5 font-medium">
            {e.fullName}
            {locked(e) && <Lock className="size-3.5 text-critical" aria-label="Cuenta bloqueada" />}
            {e.twoFactorEnabled && <ShieldCheck className="size-3.5 text-success" aria-label="Verificación en dos pasos activa" />}
          </p>
          <p className="text-xs text-muted-foreground">{e.email}</p>
        </div>
      ),
    },
    { id: 'role', header: 'Rol', sortable: true, cell: (e) => ROLE_LABELS[e.role] },
    { id: 'branch', header: 'Agencia', hideOnMobile: true, cell: (e) => (e.branchId ? (branchName.get(e.branchId) ?? EMPTY_VALUE) : 'Oficina central') },
    { id: 'lastLoginAt', header: 'Último acceso', sortable: true, hideOnMobile: true, cell: (e) => (e.lastLoginAt ? formatRelative(e.lastLoginAt) : 'Nunca') },
    { id: 'status', header: 'Estado', cell: (e) => <StatusBadge kind="active" value={String(e.isActive)} /> },
    { id: 'actions', header: '', align: 'right', cell: actions },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Administración"
        title="Usuarios"
        description="Personal con acceso: rol, agencia y estado de la cuenta."
        actions={
          <Button onClick={openNew}>
            <Plus /> Nuevo usuario
          </Button>
        }
      />
      <DataTable
        ariaLabel="Usuarios"
        columns={columns}
        rows={table.rows}
        total={table.total}
        page={table.page}
        pageSize={table.pageSize}
        onPageChange={table.setPage}
        query={table.query}
        onQueryChange={table.setQuery}
        sort={table.sort}
        onSortChange={table.setSort}
        isLoading={table.isLoading}
        isFetching={table.isFetching}
        error={table.error}
        onRetry={() => void table.refetch()}
        getRowId={(e) => e.id}
        onRowClick={(employee) => setDialog({ open: true, employee })}
        searchPlaceholder="Buscar por nombre o correo…"
        emptyTitle="Sin usuarios"
        emptyHint="Cree usuarios para su equipo y asígneles un rol."
        emptyAction={
          <Button onClick={openNew}>
            <UserPlus /> Crear usuario
          </Button>
        }
        toolbar={
          <>
            <NativeSelect aria-label="Filtrar por rol" value={role} onChange={(e) => setRole(e.target.value)} className="w-44">
              <option value={ANY}>Todos los roles</option>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </NativeSelect>
            <NativeSelect aria-label="Filtrar por estado" value={active} onChange={(e) => setActive(e.target.value)} className="w-36">
              <option value={ANY}>Todos</option>
              <option value="true">Activos</option>
              <option value="false">Inactivos</option>
            </NativeSelect>
          </>
        }
        mobileCard={(e) => (
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <p className="font-medium">{e.fullName}</p>
              <p className="text-xs text-muted-foreground">
                {ROLE_LABELS[e.role]} · {e.branchId ? (branchName.get(e.branchId) ?? EMPTY_VALUE) : 'Oficina central'}
              </p>
            </div>
            <StatusBadge kind="active" value={String(e.isActive)} />
          </div>
        )}
      />

      <EmployeeDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((d) => ({ ...d, open }))}
        employee={dialog.employee}
        branches={branches}
        currentUserId={me?.id}
      />
      <ResetPasswordDialog employee={resetting} onOpenChange={(open) => !open && setResetting(null)} />
      <PasswordConfirmDialog
        open={resettingTwoFactor !== null}
        onOpenChange={(open) => !open && setResettingTwoFactor(null)}
        title={`¿Restablecer la verificación en dos pasos de ${resettingTwoFactor?.fullName ?? ''}?`}
        description="Úselo si perdió su teléfono y sus códigos de recuperación. Se cerrarán sus sesiones y, si su rol la exige, deberá vincular un teléfono nuevo al entrar. Queda en la bitácora."
        confirmLabel="Restablecer"
        tone="destructive"
        onConfirm={async (password) => {
          if (!resettingTwoFactor) return
          await twoFactorApi.resetForEmployee(resettingTwoFactor.id, password)
          notifySuccess('Verificación en dos pasos restablecida')
          await queryClient.invalidateQueries({ queryKey: queryKeys.employees.all })
        }}
      />
      <ConfirmDialog
        open={revokingSessions !== null}
        onOpenChange={(open) => !open && setRevokingSessions(null)}
        title={`¿Cerrar todas las sesiones de ${revokingSessions?.fullName ?? ''}?`}
        description="Deberá iniciar sesión de nuevo en todos sus dispositivos. Úselo ante un equipo perdido o un acceso sospechoso."
        confirmLabel="Cerrar sesiones"
        tone="destructive"
        onConfirm={async () => {
          if (!revokingSessions) return
          const { revokedSessions } = await twoFactorApi.revokeEmployeeSessions(revokingSessions.id)
          notifySuccess('Sesiones cerradas', `${String(revokedSessions)} sesiones activas cerradas.`)
        }}
      />
      <ConfirmDialog
        open={toggling !== null}
        onOpenChange={(open) => !open && setToggling(null)}
        title={toggling?.isActive ? `¿Desactivar a ${toggling.fullName}?` : `¿Activar a ${toggling?.fullName ?? ''}?`}
        description={toggling?.isActive ? 'No podrá iniciar sesión y se cerrarán sus sesiones abiertas. Su historial se conserva.' : 'Podrá volver a iniciar sesión.'}
        confirmLabel={toggling?.isActive ? 'Desactivar' : 'Activar'}
        tone={toggling?.isActive ? 'destructive' : 'default'}
        onConfirm={() => (toggling ? toggleActive.mutateAsync(toggling) : undefined)}
      />
    </div>
  )
}
